import React, { useEffect, useState } from "react";
import { portfolioApi, marketApi } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { TID } from "@/lib/testIds";
import { toast } from "sonner";

export default function TradeExecutionPanel({ symbol, onExecuted }) {
  const [side, setSide] = useState("BUY");
  const [sym, setSym] = useState(symbol);
  const [qty, setQty] = useState("1");
  const [quote, setQuote] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { setSym(symbol); }, [symbol]);
  useEffect(() => {
    if (!sym) return;
    marketApi.quote(sym).then(setQuote).catch(() => setQuote(null));
  }, [sym]);

  const submit = async () => {
    const q = parseFloat(qty);
    if (!q || q <= 0) return toast.error("Quantity must be > 0");
    try {
      setBusy(true);
      const res = await portfolioApi.trade({ symbol: sym.toUpperCase(), side, quantity: q });
      toast.success(`${res.side} ${res.quantity} ${res.symbol} @ $${res.price}`);
      onExecuted?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Trade failed");
    } finally {
      setBusy(false);
    }
  };

  const est = quote ? (quote.price * (parseFloat(qty) || 0)).toFixed(2) : "0.00";

  return (
    <div className="aq-card p-4">
      <div className="mb-3">
        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">Paper trade</div>
        <div className="font-display text-lg font-bold mt-0.5">Execute order</div>
      </div>
      <div className="grid grid-cols-2 gap-2 mb-3">
        <button
          data-testid={TID.tradeSideBuy}
          onClick={() => setSide("BUY")}
          className={`h-9 rounded-md font-semibold text-xs transition ${side === "BUY" ? "bg-emerald-500 text-black" : "bg-white/5 text-slate-300 hover:bg-white/10"}`}
        >BUY</button>
        <button
          data-testid={TID.tradeSideSell}
          onClick={() => setSide("SELL")}
          className={`h-9 rounded-md font-semibold text-xs transition ${side === "SELL" ? "bg-rose-500 text-white" : "bg-white/5 text-slate-300 hover:bg-white/10"}`}
        >SELL</button>
      </div>
      <div className="space-y-2">
        <div>
          <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Symbol</label>
          <Input data-testid={TID.tradeSymbolInput} value={sym} onChange={(e) => setSym(e.target.value.toUpperCase())}
            className="mt-1 bg-[#0D0E14] border-white/10 font-mono" />
        </div>
        <div>
          <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Quantity</label>
          <Input data-testid={TID.tradeQtyInput} type="number" step="0.01" value={qty} onChange={(e) => setQty(e.target.value)}
            className="mt-1 bg-[#0D0E14] border-white/10 font-mono" />
        </div>
        <div className="rounded-md bg-black/40 border border-white/5 p-3 flex items-center justify-between">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Est. total</div>
          <div className="font-mono font-bold">${est}</div>
        </div>
        <Button data-testid={TID.tradeSubmit} onClick={submit} disabled={busy}
          className={`w-full h-10 rounded-md font-semibold ${side === "BUY" ? "bg-emerald-500 hover:bg-emerald-400 text-black" : "bg-rose-500 hover:bg-rose-400 text-white"}`}>
          {busy ? "Executing…" : `${side} ${sym}`}
        </Button>
      </div>
    </div>
  );
}
